import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ErroreApi, api } from "../api/client"
import { etichettaVoto } from "../api/tipi"
import type {
  Categoria,
  Edizione,
  Evento,
  MioVoto,
  Sessione,
  StatoPerformance,
  StatoSerata,
  ValueHalf,
  VotoInviato,
  VotoSvelato,
} from "../api/tipi"
import { Corolla, type PetaloAltrui } from "../componenti/Corolla"
import { Pulsantiera } from "../componenti/Pulsantiera"
import { useCanale } from "../stato/useCanale"
import { useCoda } from "../stato/useCoda"
import { useTema } from "../stato/useTema"
import "./serata.css"

type Presenze = { voted: string[]; pending: string[] }

const SCADUTA = "La sessione è scaduta: accedi di nuovo."
const FUORI = "Non fai più parte di questa giuria."

interface Props {
  sessione: Sessione
  /** Senza motivo è l'uscita scelta dal giurato; con un motivo è il server
   *  che ha chiuso la sessione, e la schermata d'accesso lo dice. */
  onEsci: (motivo?: string) => void
}

export function Serata({ sessione, onEsci }: Props) {
  const { token, edition_id } = sessione

  const [edizione, setEdizione] = useState<Edizione | null>(null)
  const [stato, setStato] = useState<StatoSerata | null>(null)
  const [mieiVoti, setMieiVoti] = useState<Record<string, MioVoto>>({})
  const [presenze, setPresenze] = useState<Presenze | null>(null)
  const [svelati, setSvelati] = useState<VotoSvelato[] | null>(null)
  const [categoriaAttiva, setCategoriaAttiva] = useState<string | null>(null)
  const [avviso, setAvviso] = useState<string | null>(null)
  const [conPulsantiera, setConPulsantiera] = useState(false)
  const [temaManuale, setTemaManuale] = useState<"giorno" | "serata" | null>(null)

  /** 401: la sessione non vale più (reset della password). 403: fuori dal
   *  gruppo. In tutti e due i casi insistere non serve: si esce. */
  const revocata = useCallback(
    (e: unknown): boolean => {
      if (!(e instanceof ErroreApi) || (e.codice !== 401 && e.codice !== 403)) return false
      onEsci(e.codice === 401 ? SCADUTA : FUORI)
      return true
    },
    [onEsci],
  )

  /** Il bottone "Esci" passa l'evento del click: qui si esce senza motivo. */
  const esci = useCallback(() => onEsci(), [onEsci])

  const serataLive = useMemo(
    () => edizione?.serate.find((s) => s.status === "live") ?? edizione?.serate[0] ?? null,
    [edizione],
  )

  const categorie = useMemo(
    () => edizione?.edition.categories.filter((c) => c.scope === "per_serata") ?? [],
    [edizione],
  )

  // Dopo una rinomina dalla regia, `sessione.juror.display_name` resta
  // quello con cui si è entrati: il nome fresco arriva con `edizione`.
  const nomeGiurato =
    edizione?.jurors.find((j) => j.id === sessione.juror.id)?.name ??
    sessione.juror.display_name

  const inCorso = stato?.current ?? null
  const svelato = svelati != null
  /** Si vota solo mentre la performance è aperta: `locked` e `revealed`
   *  restano in schermo ma non accettano più nulla. */
  const bloccato = inCorso == null || inCorso.status !== "voting"
  const inDiretta = edizione?.serate.some((s) => s.status === "live") ?? false

  // --- caricamento ---------------------------------------------------------

  const caricaEdizione = useCallback(async () => {
    try {
      setEdizione(await api.edizione(edition_id, token))
    } catch (e) {
      revocata(e)
    }
  }, [edition_id, token, revocata])

  const caricaStato = useCallback(async () => {
    if (!serataLive) return
    try {
      const fresco = await api.statoSerata(serataLive.id, token)
      setStato(fresco)
      setMieiVoti(
        Object.fromEntries((fresco.current?.my_votes ?? []).map((v) => [v.category, v])),
      )
      // Presenze e voti scoperti arrivano già dall'endpoint: senza, chi
      // ricarica vedrebbe tutti come "non ha votato" e perderebbe il reveal.
      setPresenze(
        fresco.current
          ? { voted: fresco.current.voted, pending: fresco.current.pending }
          : null,
      )
      setSvelati(fresco.current?.votes ?? null)
    } catch (e) {
      // Sessione chiusa dal server: si esce. Altrimenti la serata resta
      // com'era, e il voto passa comunque da HTTP.
      revocata(e)
    }
  }, [serataLive, token, revocata])

  useEffect(() => {
    void caricaEdizione()
  }, [caricaEdizione])

  useEffect(() => {
    void caricaStato()
  }, [caricaStato])

  useEffect(() => {
    if (categoriaAttiva == null && categorie.length > 0) setCategoriaAttiva(categorie[0].key)
  }, [categorie, categoriaAttiva])

  // --- realtime ------------------------------------------------------------

  const suEvento = useCallback(
    (e: Evento) => {
      switch (e.type) {
        case "performance_opened":
          setSvelati(null)
          setPresenze(null)
          setAvviso(null)
          void caricaStato()
          break
        case "vote_progress":
          if (e.performance_id === inCorso?.performance_id) {
            setPresenze({ voted: e.voted, pending: e.pending })
          }
          break
        case "performance_revealed":
          if (e.performance_id === inCorso?.performance_id) {
            setSvelati(e.votes)
            // Il payload contiene anche i miei voti: riconciliarli qui copre
            // il caso in cui ho votato da un altro dispositivo, o ricaricato.
            setMieiVoti((precedenti) => ({
              ...precedenti,
              ...Object.fromEntries(
                e.votes
                  .filter((v) => v.juror_id === sessione.juror.id)
                  .map((v) => [
                    v.category,
                    {
                      category: v.category,
                      status: v.status,
                      value_half: v.value_half,
                      comment: v.comment,
                    } satisfies MioVoto,
                  ]),
              ),
            }))
          }
          break
        case "performance_locked":
          // Rileggo invece di fidarmi: lo stato dice anche se nel frattempo
          // la regia ha già aperto l'artista successivo.
          if (e.performance_id === inCorso?.performance_id) void caricaStato()
          break
        case "serata_chiusa":
          void caricaEdizione()
          void caricaStato()
          break
        case "lineup_updated":
          // Può essere un'esibizione annullata, magari proprio questa.
          void caricaEdizione()
          void caricaStato()
          break
        case "juror_joined":
          void caricaEdizione()
          break
      }
    },
    [inCorso?.performance_id, sessione.juror.id, caricaStato, caricaEdizione],
  )

  const canale = useCanale(edition_id, token, suEvento, (codice) =>
    onEsci(codice === 4401 ? SCADUTA : FUORI),
  )
  const coda = useCoda(token, sessione.user.id)

  // Quando la coda si svuota i voti sono finalmente sul server: si rilegge
  // lo stato, perché nel frattempo può essere scattato un auto-reveal.
  const inCodaOra = coda.inCoda
  const codaPrecedente = useRef(inCodaOra)
  useEffect(() => {
    if (codaPrecedente.current > 0 && inCodaOra === 0) void caricaStato()
    codaPrecedente.current = inCodaOra
  }, [inCodaOra, caricaStato])

  // Se il canale è caduto si degrada in polling: meglio lento che fermo.
  useEffect(() => {
    if (canale === "collegato") return
    const t = setInterval(() => void caricaStato(), 8000)
    return () => clearInterval(t)
  }, [canale, caricaStato])

  // --- tema ----------------------------------------------------------------

  useTema(inDiretta, temaManuale)

  /** Ciclo automatico → serata → giorno. Aggiornamento funzionale: leggere
   *  `temaManuale` dalla closure sbagliava se due tocchi finivano nello
   *  stesso batch di React. */
  const cambiaTema = useCallback(() => {
    setTemaManuale((t) => (t == null ? "serata" : t === "serata" ? "giorno" : null))
  }, [])

  // --- voto ----------------------------------------------------------------

  const votoDi = (categoria: string): MioVoto | undefined => mieiVoti[categoria]

  async function inviaVoto(categoria: string, modifica: Partial<MioVoto>) {
    if (!inCorso || !serataLive) return
    const precedente = votoDi(categoria)
    const nuovo: MioVoto = {
      category: categoria,
      status: modifica.status ?? precedente?.status ?? "scored",
      value_half:
        modifica.value_half !== undefined ? modifica.value_half : (precedente?.value_half ?? null),
      comment: modifica.comment !== undefined ? modifica.comment : (precedente?.comment ?? null),
    }

    const daInviare: VotoInviato = {
      artist_id: inCorso.artist.id,
      serata_id: serataLive.id,
      category: categoria,
      status: nuovo.status,
      value_half: nuovo.value_half,
      comment: nuovo.comment,
      client_ts: new Date().toISOString(),
    }

    // Ottimismo: il voto compare subito e si corregge solo se il server rifiuta.
    setMieiVoti((v) => ({ ...v, [categoria]: nuovo }))
    setAvviso(null)

    try {
      await api.vota(daInviare, token)
    } catch (e) {
      if (revocata(e)) return
      // Rete assente non è un rifiuto: il voto resta sullo schermo e va in
      // coda. Un 409 invece è definitivo e si torna indietro davvero.
      if (e instanceof ErroreApi && e.codice === 0) {
        await coda.metti(daInviare)
        setAvviso("Voto messo in coda: riparte da solo quando torna la rete.")
        return
      }
      setMieiVoti((v) => {
        const ripristinato = { ...v }
        if (precedente) ripristinato[categoria] = precedente
        else delete ripristinato[categoria]
        return ripristinato
      })
      setAvviso(spiegaErrore(e))
    }
  }

  // --- rendering -----------------------------------------------------------

  if (!edizione) return <div className="schermo serata__attesa">Carico la serata…</div>

  if (!inCorso) {
    return (
      <div className="schermo">
        <Testata
          canale={canale}
          giurato={nomeGiurato}
          inCoda={coda.inCoda}
          temaManuale={temaManuale}
          onTema={cambiaTema}
          onEsci={esci}
        />
        <div className="scheda serata__pausa">
          <p className="occhiello">Serata {serataLive?.number ?? "—"}</p>
          <h2 className="serata__pausa-titolo">Nessuno sta cantando</h2>
          <p className="serata__pausa-nota">
            Appena la regia apre l'esibizione successiva, questa schermata cambia da sola.
          </p>
        </div>
      </div>
    )
  }

  const categoria = categorie.find((c) => c.key === categoriaAttiva) ?? categorie[0]
  const voto = categoria ? votoDi(categoria.key) : undefined

  return (
    <div className="schermo serata">
      <Testata
        canale={canale}
        giurato={nomeGiurato}
        inCoda={coda.inCoda}
        temaManuale={temaManuale}
        onTema={cambiaTema}
        onEsci={esci}
      />

      <Cartello
        posizione={inCorso.position}
        nome={inCorso.artist.name}
        brano={inCorso.artist.song}
        stato={inCorso.status}
      />

      <Presenza giurati={edizione.jurors} presenze={presenze} />

      {categoria && (
        <>
          <SceltaCategoria
            categorie={categorie}
            attiva={categoria.key}
            voti={mieiVoti}
            onScegli={setCategoriaAttiva}
          />

          {conPulsantiera ? (
            <Pulsantiera
              valore={voto?.value_half ?? null}
              nc={voto?.status === "nc"}
              disabilitato={bloccato}
              onConferma={(v) => inviaVoto(categoria.key, { status: "scored", value_half: v })}
            />
          ) : (
            <Corolla
              valore={voto?.value_half ?? null}
              nc={voto?.status === "nc"}
              famiglia={`--${categoria.key}`}
              etichetta={categoria.label}
              disabilitato={bloccato}
              onConferma={(v) => inviaVoto(categoria.key, { status: "scored", value_half: v })}
              fioritura={svelati ? petaliAltrui(svelati, categoria.key, sessione.juror.id) : null}
              media={svelati ? mediaDi(svelati, categoria.key) : null}
            />
          )}

          <div className="serata__azioni">
            <button
              type="button"
              className={`bottone ${voto?.status === "nc" ? "acceso" : ""}`}
              aria-pressed={voto?.status === "nc"}
              disabled={bloccato}
              onClick={() => inviaVoto(categoria.key, { status: "nc", value_half: null })}
            >
              NC
            </button>
            <button
              type="button"
              className="bottone serata__cambia-controllo"
              onClick={() => setConPulsantiera((c) => !c)}
            >
              {conPulsantiera ? "Usa la corolla" : "Usa i tasti"}
            </button>
          </div>

          {svelato && (
            <Legenda
              voti={svelati!}
              categoria={categoria.key}
              giurati={edizione.jurors}
              mioId={sessione.juror.id}
            />
          )}

          <Commento
            valore={voto?.comment ?? ""}
            attivo={voto != null && !bloccato}
            segnaposto={
              bloccato
                ? "I voti di questa esibizione sono chiusi"
                : voto == null
                  ? "Prima dai un voto"
                  : "Le battute sono metà del divertimento"
            }
            onSalva={(testo) => inviaVoto(categoria.key, { comment: testo || null })}
          />
        </>
      )}

      {avviso && <p className="avviso">{avviso}</p>}
      {svelato && <CommentiAltrui voti={svelati!} mioId={sessione.juror.id} />}
    </div>
  )
}

// --- pezzi ------------------------------------------------------------------

function Testata({
  canale,
  giurato,
  inCoda,
  temaManuale,
  onTema,
  onEsci,
}: {
  canale: string
  giurato: string
  inCoda: number
  temaManuale: "giorno" | "serata" | null
  onTema: () => void
  onEsci: () => void
}) {
  const descrizioneTema =
    temaManuale == null
      ? "Tema automatico, tocca per forzare Serata"
      : temaManuale === "serata"
        ? "Tema Serata, tocca per passare a Giorno"
        : "Tema Giorno, tocca per tornare automatico"
  return (
    <header className="serata__testata">
      <span className="serata__giurato">
        {giurato}
        {canale !== "collegato" && (
          <span className="serata__scollegato" title="Canale in ripresa">
            {" "}
            · fuori linea
          </span>
        )}
        {inCoda > 0 && (
          <span className="serata__coda">
            {inCoda} {inCoda === 1 ? "voto in coda" : "voti in coda"}
          </span>
        )}
      </span>
      <span className="serata__comandi">
        <button type="button" className="serata__icona" aria-label={descrizioneTema} onClick={onTema}>
          ◐
        </button>
        <button type="button" className="serata__icona" aria-label="Esci" onClick={onEsci}>
          ⏻
        </button>
      </span>
    </header>
  )
}

/** Il cartello: il lower-third della grafica televisiva. Zona di sola
 *  lettura, in alto, dove il pollice non arriva (DESIGN.md §5). */
function Cartello({
  posizione,
  nome,
  brano,
  stato,
}: {
  posizione: number
  nome: string
  brano: string | null
  stato: StatoPerformance
}) {
  const titolo = {
    pending: "Sta per cantare",
    voting: "Adesso canta",
    locked: "Voti chiusi, si aspetta",
    revealed: "Voti scoperti",
  }[stato]

  return (
    <div className={`cartello ${stato === "revealed" ? "cartello--svelato" : ""}`} key={nome}>
      <p className="occhiello">
        {titolo} · {posizione}ª esibizione
      </p>
      <h1 className="cartello__nome">{nome}</h1>
      {brano && <p className="cartello__brano">{brano}</p>}
    </div>
  )
}

/** Chi ha votato — mai cosa ha votato. */
function Presenza({
  giurati,
  presenze,
}: {
  giurati: { id: string; name: string; emoji: string }[]
  presenze: Presenze | null
}) {
  const haVotato = new Set(presenze?.voted ?? [])
  return (
    <div className="presenza" aria-label="Chi ha già votato">
      {giurati.map((g) => (
        <span
          key={g.id}
          className={`presenza__chip ${haVotato.has(g.name) ? "presenza__chip--fatto" : ""}`}
          title={`${g.name}${haVotato.has(g.name) ? " ha votato" : " non ha ancora votato"}`}
        >
          <span aria-hidden="true">{g.emoji}</span>
          <span className="presenza__nome">{g.name}</span>
        </span>
      ))}
    </div>
  )
}

function SceltaCategoria({
  categorie,
  attiva,
  voti,
  onScegli,
}: {
  categorie: Categoria[]
  attiva: string
  voti: Record<string, MioVoto>
  onScegli: (k: string) => void
}) {
  return (
    <div className="scelta" role="tablist" aria-label="Categoria da votare">
      {categorie.map((c) => (
        <button
          key={c.key}
          type="button"
          role="tab"
          aria-selected={c.key === attiva}
          className="scelta__voce"
          data-famiglia={c.key}
          onClick={() => onScegli(c.key)}
        >
          {c.label}
          {voti[c.key] && <span className="scelta__spunta" aria-label="già votato" />}
        </button>
      ))}
    </div>
  )
}

/** Il commento si abilita solo dopo un voto: senza uno stato da allegare
 *  non c'è nulla da inviare al server. */
/** I voti della categoria attiva, in chiaro. Sta qui e non sull'arco perché
 *  i nomi ruotati agli estremi finivano fuori dal riquadro, e con otto
 *  giurati si accavallavano comunque. */
function Legenda({
  voti,
  categoria,
  giurati,
  mioId,
}: {
  voti: VotoSvelato[]
  categoria: string
  giurati: { id: string; name: string; emoji: string }[]
  mioId: string
}) {
  const emojiDi = new Map(giurati.map((g) => [g.name, g.emoji]))
  const dellaCategoria = voti
    .filter((v) => v.category === categoria)
    .sort((a, b) => (b.value_half ?? -1) - (a.value_half ?? -1))

  if (dellaCategoria.length === 0) return null

  return (
    <ul className="legenda">
      {dellaCategoria.map((v) => (
        <li
          key={v.juror_id}
          className={`legenda__voce ${v.juror_id === mioId ? "legenda__voce--mia" : ""}`}
        >
          <span aria-hidden="true">{emojiDi.get(v.juror_name) ?? "🎤"}</span>
          <span className="legenda__nome">{v.juror_name}</span>
          <b className={v.status === "nc" ? "legenda__nc" : "legenda__valore"}>
            {v.status === "nc" ? "NC" : etichettaVoto(v.value_half as ValueHalf)}
          </b>
        </li>
      ))}
    </ul>
  )
}

function Commento({
  valore,
  attivo,
  segnaposto,
  onSalva,
}: {
  valore: string
  attivo: boolean
  segnaposto: string
  onSalva: (testo: string) => void
}) {
  const [bozza, setBozza] = useState(valore)
  const ultimoSalvato = useRef(valore)

  useEffect(() => {
    setBozza(valore)
    ultimoSalvato.current = valore
  }, [valore])

  return (
    <label className="commento">
      <span className="occhiello">Commento a margine</span>
      <textarea
        className="campo commento__campo"
        value={bozza}
        maxLength={280}
        rows={2}
        disabled={!attivo}
        placeholder={segnaposto}
        onChange={(e) => setBozza(e.target.value)}
        onBlur={() => {
          if (bozza !== ultimoSalvato.current) {
            ultimoSalvato.current = bozza
            onSalva(bozza.trim())
          }
        }}
      />
    </label>
  )
}

function CommentiAltrui({ voti, mioId }: { voti: VotoSvelato[]; mioId: string }) {
  const conCommento = voti.filter((v) => v.comment && v.juror_id !== mioId)
  if (conCommento.length === 0) return null
  return (
    <div className="scheda commenti">
      <p className="occhiello">Cos'hanno scritto gli altri</p>
      <ul className="commenti__lista">
        {conCommento.map((v, k) => (
          <li key={k}>
            <b>{v.juror_name}</b> <span className="commenti__cat">{v.category}</span>
            <br />
            {v.comment}
          </li>
        ))}
      </ul>
    </div>
  )
}

// --- utilità ----------------------------------------------------------------

function petaliAltrui(voti: VotoSvelato[], categoria: string, mioId: string): PetaloAltrui[] {
  return voti
    .filter((v) => v.category === categoria && v.status === "scored" && v.value_half != null)
    .map((v) => ({
      nome: v.juror_id === mioId ? "tu" : v.juror_name,
      valueHalf: v.value_half as ValueHalf,
    }))
}

/** Media in scala 1-10. Gli NC restano fuori: non sono uno zero. */
function mediaDi(voti: VotoSvelato[], categoria: string): number | null {
  const valori = voti
    .filter((v) => v.category === categoria && v.status === "scored" && v.value_half != null)
    .map((v) => v.value_half as number)
  if (valori.length === 0) return null
  return valori.reduce((s, v) => s + v, 0) / valori.length / 2
}

function spiegaErrore(e: unknown): string {
  if (e instanceof ErroreApi) {
    if (e.troppoTardi) return "Troppo tardi: i voti per questa esibizione sono chiusi."
    if (e.codice === 0) return "Voto non inviato, manca la rete. Riprova fra un momento."
    if (e.codice === 404) return "Questo artista non risulta in scaletta stasera."
    return e.message
  }
  return "Il voto non è stato registrato."
}
